import { ScreenSaverMediaType } from '@/enums/home';
import { useConfigStore } from '@/stores';
import type { CacheScreenSaverInfo } from '@/types';
import useEmblaCarousel from 'embla-carousel-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type Props = {
    screenSaver: CacheScreenSaverInfo[];
};

export const HomePageSwiper = ({ screenSaver }: Props) => {
    const isZoomIn = useConfigStore((state) => state.config.ui.zoomIn);
    const isZoomClosed = useConfigStore((state) => state.config.ui.zoomClosed);

    const isZoomed = useMemo(() => {
        return isZoomIn || (isZoomClosed === false && isZoomIn === false);
    }, [isZoomIn, isZoomClosed]);

    const [viewPortRef, sliderApi] = useEmblaCarousel({
        containScroll: 'trimSnaps',
        dragFree: false,
        loop: true,
        watchDrag: !isZoomed, // Disable drag when zoomed in
    });

    const [currentSlide, setCurrentSlide] = useState(0);
    const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [mediaErrors, setMediaErrors] = useState<Set<string>>(new Set());

    const handleMediaError = useCallback((itemId: string) => {
        setMediaErrors((prev) => new Set(prev).add(itemId));
        console.warn(`Media error for ${itemId}, falling back to original URL`);
    }, []);

    const getMediaUrl = useCallback(
        (item: CacheScreenSaverInfo) => {
            if (mediaErrors.has(item.id) || !item.localPath) {
                return item.originalData.fileUrl;
            }
            return `/screen-savers/${item.localPath}`;
        },
        [mediaErrors]
    );

    const goToNextSlide = useCallback(() => {
        if (isZoomed) {
            return;
        }

        if (!Array.isArray(screenSaver) || !screenSaver?.length) {
            return;
        }

        // Nếu chỉ có 1 slide, restart video nếu là video
        if (screenSaver.length === 1) {
            const currentVideoRefs = videoRefs.current;
            const video = currentVideoRefs[0];
            if (video) {
                video.currentTime = 0;
                const playPromise = video.play();
                if (playPromise !== undefined) {
                    playPromise.catch(() => {});
                }
            }
            return;
        }

        // Chuyển sang slide tiếp theo nếu có nhiều hơn 1 slide
        if (sliderApi && screenSaver.length > 1) {
            const nextIndex = (currentSlide + 1) % screenSaver.length;
            sliderApi.scrollTo(nextIndex);
            setCurrentSlide(nextIndex);
        }
    }, [sliderApi, currentSlide, screenSaver, isZoomed]);

    const handleSlideChange = useCallback(() => {
        if (sliderApi) {
            const selectedIndex = sliderApi.selectedScrollSnap();
            setCurrentSlide(selectedIndex);
        }
    }, [sliderApi]);

    useEffect(() => {
        if (sliderApi) {
            sliderApi.on('select', handleSlideChange);
            return () => {
                sliderApi.off('select', handleSlideChange);
            };
        }
    }, [sliderApi, isZoomed, handleSlideChange]);

    useEffect(() => {
        setMediaErrors(new Set());
    }, [screenSaver]);

    useEffect(() => {
        if (!Array.isArray(screenSaver) || !screenSaver.length || isZoomed) {
            return;
        }

        const currentItem = screenSaver[currentSlide];

        if (!currentItem) return;
        const currentVideoRefs = videoRefs.current;

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }

        if (currentItem?.originalData?.mediaType === ScreenSaverMediaType.VIDEO) {
            const video = currentVideoRefs[currentSlide];
            if (video) {
                // Đảm bảo video đã load trước khi play
                const handleVideoCanPlay = () => {
                    video.currentTime = 0;
                    const playPromise = video.play();
                    if (playPromise !== undefined) {
                        playPromise.catch(() => {});
                    }
                };

                const handleVideoEnd = () => {
                    goToNextSlide();
                };

                // Nếu video đã sẵn sàng, play ngay
                if (video.readyState >= 2) {
                    handleVideoCanPlay();
                } else {
                    video.addEventListener('canplay', handleVideoCanPlay, { once: true });
                }

                video.addEventListener('ended', handleVideoEnd);

                return () => {
                    video.removeEventListener('canplay', handleVideoCanPlay);
                    video.removeEventListener('ended', handleVideoEnd);
                    video.pause();
                    video.currentTime = 0;
                };
            }
        } else if (currentItem?.originalData?.mediaType === ScreenSaverMediaType.IMAGE) {
            // Nếu có playtimeSec thì dùng, nếu không thì dùng giá trị mặc định 5 giây
            const playtime = currentItem?.originalData?.playtimeSec || 5;
            timeoutRef.current = setTimeout(() => {
                goToNextSlide();
            }, playtime * 1000);
        }

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, [currentSlide, goToNextSlide, screenSaver, isZoomed]);

    // Cleanup on unmount
    useEffect(() => {
        const currentVideoRefs = videoRefs.current;

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            // Pause all videos
            currentVideoRefs.forEach((video) => {
                if (video) {
                    video.pause();
                    video.currentTime = 0;
                }
            });
        };
    }, []);

    return (
        <div className="h-full w-full overflow-hidden" ref={viewPortRef}>
            <div className="flex h-full w-full">
                {Array.isArray(screenSaver) &&
                    screenSaver.length > 0 &&
                    screenSaver.map((item, index) => (
                        <div key={item.id} className="h-full w-full flex-shrink-0">
                            {item?.originalData?.mediaType === ScreenSaverMediaType.IMAGE && (
                                <img
                                    className="widescreen:h-full h-[calc(1450*var(--vw-unit))] w-full object-cover"
                                    src={getMediaUrl(item)}
                                    alt={item.id}
                                    onError={() => handleMediaError(item.id)}
                                />
                            )}
                            {item?.originalData?.mediaType === ScreenSaverMediaType.VIDEO && (
                                <video
                                    ref={(el) => {
                                        videoRefs.current[index] = el;
                                    }}
                                    className="widescreen:h-full h-[calc(1450*var(--vw-unit))] w-full object-cover"
                                    src={getMediaUrl(item)}
                                    autoPlay={false}
                                    muted
                                    playsInline
                                    onError={() => handleMediaError(item.id)}
                                />
                            )}
                        </div>
                    ))}
            </div>
        </div>
    );
};
